import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Target } from "lucide-react";
import { exportCSV } from "@/lib/exports";
import { ExportBtn } from "@/components/dashboard/ExportBtn";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const LABELS = { ex_showroom: "Ex-Showroom", kit: "Kit", riao_finance: "RIAO Finance", riao_cash: "RIAO Cash", seat_cover: "Seat Cover", side_bag: "Side Bag", jaal: "Jaal", helmet: "Helmet", asd_discount: "ASD Disc.", customer_discount: "Cust. Disc." };
const DEDUCT = ["asd_discount", "customer_discount"];

const AgeBar = ({ m }) => {
  const total = m.in_stock || 1;
  return (
    <div className="w-28">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-800">
        <div className="bg-emerald-500" style={{ width: `${(m.fresh / total) * 100}%` }} />
        <div className="bg-amber-500" style={{ width: `${(m.watch / total) * 100}%` }} />
        <div className="bg-rose-500" style={{ width: `${(m.old / total) * 100}%` }} />
      </div>
      {m.old > 0 && <p className="text-[9px] text-rose-400 mt-0.5 font-mono">oldest {m.oldest_age}d</p>}
    </div>
  );
};

export const FocusBoard = () => {
  const [rows, setRows] = useState(null);
  const [params, setParams] = useState(Object.keys(LABELS));
  useEffect(() => { api.get("/models/focus-board").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows) return <div className="h-96 bg-[#1E293B] border border-[#334155] rounded-xl animate-pulse" />;

  const total = (p) => params.reduce((t, k) => t + (DEDUCT.includes(k) ? -Number(p[k] || 0) : Number(p[k] || 0)), 0);
  const toggle = (k) => setParams(params.includes(k) ? params.filter((x) => x !== k) : [...params, k]);
  const sums = rows.reduce((t, m) => ({ s: t.s + m.in_stock, f: t.f + m.fresh, w: t.w + m.watch, o: t.o + m.old }), { s: 0, f: 0, w: 0, o: 0 });

  return (
    <div data-testid="focus-board" className="bg-[#1E293B] border border-[#334155] rounded-xl p-4 sm:p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base sm:text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <Target className="h-5 w-5 text-rose-400" /> Model Focus Board
        </h3>
        <ExportBtn onCSV={() => exportCSV(rows.map((m) => ({ model: m.model, code: m.code, stock: m.in_stock, fresh: m.fresh, watch: m.watch, old: m.old, oldest_age: m.oldest_age, total: total(m.pricing) })), "focus-board")} />
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Tap stock for colours · tap price for breakup · 🔴 sell these first</p>
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {Object.entries(LABELS).map(([k, l]) => (
          <button key={k} data-testid={`fb-param-${k}`} onClick={() => toggle(k)}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${params.includes(k)
              ? "border-cyan-500/50 text-cyan-400 bg-cyan-500/10" : "border-slate-700 text-slate-500 line-through"}`}>
            {params.includes(k) ? "✓ " : ""}{l}
          </button>
        ))}
        <button data-testid="fb-param-all" onClick={() => setParams(Object.keys(LABELS))} className="text-[10px] text-cyan-400 ml-1 hover:underline">All</button>
        <span className="text-slate-600 text-[10px]">·</span>
        <button data-testid="fb-param-none" onClick={() => setParams([])} className="text-[10px] text-slate-400 hover:underline">None</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-widest text-slate-400 border-b-2 border-[#334155]">
              <th className="pb-2 pr-2">Model (Code)</th>
              <th className="pb-2 pr-2">Stock 👆</th>
              <th className="pb-2 pr-2">Ageing</th>
              <th className="pb-2 pr-2 text-right">Price 👆</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={m.model} data-testid={`fb-row-${i}`} className={`border-b border-slate-800/50 ${m.focus === "sell_first" ? "bg-rose-500/5" : ""}`}>
                <td className="py-2.5 pr-2 font-semibold text-slate-200">{m.model} <span className="text-slate-500 text-xs font-mono">({m.code})</span></td>
                <td className="py-2.5 pr-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={`fb-stock-${i}`} className="font-mono font-bold text-blue-400 underline decoration-dotted underline-offset-4">{m.in_stock}</button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3 bg-[#0B0F17] border-[#334155] text-slate-100 text-xs" align="start">
                      <p className="font-bold mb-2">{m.model} — {m.in_stock} in stock</p>
                      {m.colours.map((c) => (
                        <div key={c.colour} className="flex justify-between py-1 border-b border-slate-800/60">
                          <span className="text-slate-300">{c.colour} × {c.count}</span>
                          <span className={`font-mono ${c.oldest > 60 ? "text-rose-400" : c.oldest > 30 ? "text-amber-400" : "text-emerald-400"}`}>{c.oldest}d</span>
                        </div>
                      ))}
                      <p className="text-slate-500 mt-2 font-mono text-[10px]">Oldest: {m.oldest_chassis} · {m.oldest_age}d</p>
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="py-2.5 pr-2"><AgeBar m={m} /></td>
                <td className="py-2.5 pr-2 text-right">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={`fb-price-${i}`} className="font-mono font-bold text-emerald-400 underline decoration-dotted underline-offset-4">
                        {Object.keys(m.pricing).length ? inr(total(m.pricing)) : "—"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-3 bg-[#0B0F17] border-[#334155] text-slate-100 text-xs" align="end">
                      <p className="font-bold mb-2">{m.model} ({m.code})</p>
                      {Object.entries(LABELS).filter(([k]) => params.includes(k) && m.pricing[k]).map(([k, l]) => (
                        <div key={k} className="flex justify-between py-1 border-b border-slate-800/60">
                          <span className="text-slate-400">{l}</span>
                          <span className={`font-mono ${DEDUCT.includes(k) ? "text-rose-400" : "text-slate-200"}`}>{DEDUCT.includes(k) ? "−" : ""}{inr(m.pricing[k])}</span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 font-bold text-emerald-400">
                        <span>On-road Total</span><span className="font-mono">{inr(total(m.pricing))}</span>
                      </div>
                    </PopoverContent>
                  </Popover>
                </td>
                <td className="py-2.5 pl-2">
                  {m.focus === "sell_first" && <Badge data-testid={`fb-focus-${i}`} className="bg-rose-500/15 text-rose-400 border border-rose-500/40 text-[9px] font-extrabold">🔴 SELL FIRST</Badge>}
                  {m.focus === "ageing" && <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/40 text-[9px] font-extrabold">🟠 AGEING</Badge>}
                  {!m.focus && <span className="text-[10px]">🟢</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-testid="fb-total-row" className="border-t-2 border-[#334155] font-bold">
              <td className="py-2.5 pr-2 text-slate-100">Total · {rows.length} models</td>
              <td className="py-2.5 pr-2 font-mono text-amber-400">{sums.s}</td>
              <td className="py-2.5 pr-2 text-[10px] text-slate-400 font-mono">{sums.f} / {sums.w} / {sums.o}</td>
              <td></td><td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
